/**
 * Level document format (editor source of truth).
 *
 * Coordinates: Unity world units, y up. 1 tile = 1 unit (16px sprites at 16 PPU).
 * Tile cell (x, y) covers [x, x+1] x [y, y+1] in its layer's local space.
 *
 * Unity fidelity: placed prefab instances keep raw Unity property modifications
 * (`mods`) addressed by the fileID of the target object inside the source prefab,
 * exactly like a .unity file does. Editor UIs edit those mods through prefab
 * property schemas, play mode merges them over the prefab defaults, and the Unity
 * exporter feeds them to PrefabUtility.SetPropertyModifications.
 */

export const FORMAT_VERSION = 1;

export type Vec2 = [number, number];

/** References between entities inside a level: "obj:<id>", "body:<id>", "layer:<id>". */
export type EntityRef = string;

export interface PaletteEntry {
  /** texture GUID */
  tex: string;
  /** sprite name inside the texture */
  sprite: string;
  /** optional Unity tile asset GUID (Tile/AnimatedTile) that renders this sprite */
  tile?: string;
}

/**
 * Tile transform flags (Unity tile matrix restricted to the 8 grid symmetries):
 * bit0 = flip X, bit1 = flip Y, bit2 = swap axes (applied before flips).
 */
export type TileFlags = number;

export interface TileCell {
  /** palette index; -1 = explicitly erased (hides generated terrain tiles in this cell) */
  p: number;
  f: TileFlags;
}

export type LayerRole =
  | 'level' // solid collision (TilemapCollider2D + CompositeCollider2D)
  | 'foreground'
  | 'background'
  | 'decor' // no collision, any sorting layer
  | 'spikes' // SpikeTilemapGeneratorFlag
  | 'oneway'; // OneWayColliderGeneratorFlag

export interface TileLayer {
  id: string;
  name: string;
  role: LayerRole;
  /** Unity sorting layer name */
  sortingLayer: string;
  sortingOrder: number;
  /** tilemap tint (Tilemap.m_Color), rgba 0..1 */
  color?: [number, number, number, number];
  /** local offset of the tilemap transform (usually 0,0) */
  offset?: Vec2;
  visible?: boolean;
  locked?: boolean;
  /** extra flags that become components on export */
  spikeGenerator?: boolean;
  oneWayGenerator?: boolean;
  /** collider on export (defaults: level/oneway = true) */
  collider?: boolean;
  /** sparse cells: flat array [x, y, p, f, x, y, p, f, ...] */
  tiles: number[];
  /** Unity provenance for re-export (fileID of the tilemap inside its prefab, when the layer belongs to a prefab body) */
  unitySourceId?: string;
  /** extra Unity components on the tilemap GameObject (e.g. ColorPropertyBlockController) */
  extraComponents?: CustomComponent[];
}

export interface ShadingSettings {
  enabled: boolean;
  /** tiles from the surface before the dark interior starts */
  depth: number;
  seed: number;
  /** 0..1 randomness of the dark boundary */
  jitter: number;
}

export interface DecorationSettings {
  enabled: boolean;
  seed: number;
  /** master multiplier on every decoration's learned chance */
  density: number;
  /** extra multiplier per foliage kind (missing: 1) */
  kinds?: Partial<Record<FoliageKind, number>>;
  foreground: boolean;
  background: boolean;
  /** surface grass / edge art: learned gaps (auto), everywhere the art has a piece (full) or off (none) */
  edge?: EdgeArtMode;
}

export type EdgeArtMode = 'auto' | 'full' | 'none';

/** What a learned decoration stamp is, for per-kind density and the foliage brushes. */
export type FoliageKind = 'grass' | 'flowers' | 'vines' | 'trees' | 'rocks' | 'props' | 'details';
export const FOLIAGE_KINDS: FoliageKind[] = ['grass', 'flowers', 'vines', 'trees', 'rocks', 'props', 'details'];
export const FOLIAGE_LABELS: Record<FoliageKind, string> = {
  grass: 'Grass tufts',
  flowers: 'Flowers',
  vines: 'Vines',
  trees: 'Trees',
  rocks: 'Rocks',
  props: 'Props',
  details: 'Terrain details',
};
export const foliageBit = (k: FoliageKind) => 1 << FOLIAGE_KINDS.indexOf(k);
export const ALL_FOLIAGE = (1 << FOLIAGE_KINDS.length) - 1;
/** What the "any foliage" brush paints: everything but terrain details (pockets and edges cut into the dirt). */
export const ANY_FOLIAGE = ALL_FOLIAGE & ~foliageBit('details');

/**
 * Hand-painted grass / edge art of a container, applied over the generated result: flat [x, y, v, ...]
 * cells (container-local tile coordinates), v = 1 grass, 0 no grass. Only surface cells are affected.
 */
export type GrassPaint = number[];

/** The outline lines of a terrain cell that grass can be marked on: its four sides and its own slope. */
export const LINE_L = 1, LINE_R = 2, LINE_B = 4, LINE_T = 8, LINE_S = 16;
export const LINE_BITS = 5;
export const ALL_LINES = 31;

/**
 * Hand-painted grass per outline line of a cell, over the cell's grass (GrassPaint or the shape setting):
 * flat [x, y, v, ...] cells (container-local), v = on | off << LINE_BITS with LINE_* bits. Lines without a
 * mark follow the cell. Kept apart from GrassPaint so editors without per-line grass ignore it.
 */
export type GrassLines = number[];

/**
 * Hand-painted spikes per outline line of a cell (same packing as GrassLines). Default is off.
 * Visuals sit in the empty neighbour of each marked side; slopes (`LINE_S`) are ignored.
 */
export type SpikeLines = number[];

/** Decoration layers a foliage paint value covers: front = foreground and level-layer decorations, back = background. */
export type FoliageLayer = 'front' | 'back';
export const FOLIAGE_SHIFT: Record<FoliageLayer, number> = { front: 0, back: 16 };

/**
 * Hand-painted foliage of a container: flat [x, y, v, ...] cells (container-local) with, per decoration
 * layer, v = (add | remove << 8) << FOLIAGE_SHIFT[layer], one bit per FOLIAGE_KINDS entry. Added kinds are
 * placed wherever their decorations fit when rooted (anchor or the ground under them) in a painted cell;
 * removed kinds never draw on or cover a painted cell.
 */
export type FoliagePaint = number[];

/** The paint fields of one decoration layer in a value: [add, remove]. */
export const foliageFields = (v: number, layer: FoliageLayer): [number, number] => {
  const f = v >>> FOLIAGE_SHIFT[layer];
  return [f & 255, (f >>> 8) & 255];
};

export interface TerrainShape {
  id: string;
  name?: string;
  /** terrain style id (see TerrainStyle in tileset data) */
  style: string;
  /** colour variant name of the style (see terrainStyles().variants); default = first variant */
  variant?: string;
  mode: 'add' | 'subtract';
  /** rings of integer grid points; ring 0 = outline, further rings = holes */
  rings: Vec2[][];
  shading: ShadingSettings;
  decoration: DecorationSettings;
  /** target layer ids (inside the same container) */
  targets: { level: string; foreground?: string; background?: string };
  hidden?: boolean;
}

export interface PropMod {
  /** fileID of the target object/component inside the source prefab */
  t: string;
  /** Unity propertyPath */
  p: string;
  /** serialized value (string, as in Unity YAML); ignored when r is set */
  v?: string;
  /** object reference: EntityRef for scene objects, or "asset:<guid>:<fileID>" for assets */
  r?: string;
}

export interface CustomComponent {
  /** Unity component type name (MonoBehaviour class name or built-in type) */
  type: string;
  /** full serialized field data (Unity YAML converted to JSON, values as strings) */
  data: Record<string, any>;
}

export interface PlacedObject {
  id: string;
  name: string;
  /** prefab GUID; null for custom GameObjects built from components */
  prefab: string | null;
  x: number;
  y: number;
  /** z rotation in degrees */
  rotation: number;
  scale: Vec2;
  /** z position (rarely used, kept for round trip) */
  z?: number;
  mods: PropMod[];
  /** custom GameObjects: components to add */
  components?: CustomComponent[];
  active?: boolean;
  /** Unity hierarchy folder path the object lives under, e.g. "Level/Coins" */
  folder?: string;
  /** For objects parented under a prefab instance's child: owner object id + child path */
  attach?: { object: string; path: string };
}

export interface MovementVars {
  speedOverride: number;
  accOverride: number;
  speedEnum: number; // MoveSpeed enum
  accEnum: number; // MoveAcceleration enum
  returnSpeedOverride: number;
  returnAccOverride: number;
  returnSpeedEnum: number;
  returnAccEnum: number;
}

export interface Waypoint {
  id: string;
  name: string;
  /** world position of the platform content when it reaches this point */
  x: number;
  y: number;
  movementVars: MovementVars;
  pauseOnArrivalForSeconds: number;
  pausePlatformOnArrival: boolean;
  warpToNextPoint: boolean;
  /** Unity provenance (fileID of the MoveTo transform's GameObject inside the source prefab) */
  unitySourceId?: string;
}

export interface MovingPlatformProps {
  activators: EntityRef[];
  activationType: number; // 0 All, 1 Any
  defaultMovementVars: MovementVars;
  deactivationAction: number; // DeactivationAction
  finishAction: number; // FinishAction
  dontUseStartAsMovementPoint: boolean;
  /** PlatformMovePoint settings on the start point (the moving content itself) */
  start: Omit<Waypoint, 'id' | 'name' | 'x' | 'y' | 'unitySourceId'>;
  waypoints: Waypoint[];
}

export type BodyBehavior = 'movingPlatform' | 'fadeOut' | 'rotating' | 'controlled' | 'static';

export type BodyPlacement = 'behindLevel' | 'coveringLevel';

export interface Body {
  id: string;
  name: string;
  behavior: BodyBehavior;
  placement: BodyPlacement;
  /** world position of the moving content root ("MovingPlatform" transform) */
  origin: Vec2;
  /** content rotation in degrees (rotating bodies) */
  rotation?: number;
  layers: TileLayer[];
  terrains: TerrainShape[];
  grassPaint?: GrassPaint;
  grassLines?: GrassLines;
  spikeLines?: SpikeLines;
  decoPaint?: FoliagePaint;
  /** @deprecated first foliage paint (add | remove << 8 for both layers); read once into decoPaint */
  foliagePaint?: number[];
  /** objects riding on the body (positions are world positions at the start pose) */
  objects: PlacedObject[];
  moving?: MovingPlatformProps;
  /**
   * Behavior-specific Unity data for non moving-platform bodies (FadeOutHandler / RigidBodyRotator /
   * BodyControlHandler fields), stored as raw mods against the body prefab.
   */
  mods: PropMod[];
  /** prefab the body is exported from (defaults by behavior when absent) */
  prefab?: string;
  /** unpacked bodies (no prefab): extra components on the body root (FadeOutHandler, RigidBodyRotator, ...) */
  components?: CustomComponent[];
  /** body lives inside another placed object's prefab instance (doors) */
  owner?: { object: string; handlerSourceId: string };
  /** handler root world position (MovingPlatformHandler transform), defaults to origin */
  handlerPosition?: Vec2;
  folder?: string;
  /** kinematic rigidbody settings */
  rigidbody?: { bodyType: number };
  hidden?: boolean;
  locked?: boolean;
}

export interface ParallaxSettings {
  /** parallax prefab GUID or null for none */
  prefab: string | null;
  mods: PropMod[];
  x: number;
  y: number;
}

export interface LevelSettings {
  /** Main Camera background colour */
  backgroundColor: [number, number, number, number];
  parallax: ParallaxSettings;
}

/** Objects that every level has and that the settings panel edits. */
export type SystemRole = 'camera' | 'levelScripts' | 'necessary';

export interface LevelDoc {
  format: 'platformer-level';
  version: number;
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
  source?: { unityScene?: string };
  settings: LevelSettings;
  palette: PaletteEntry[];
  layers: TileLayer[];
  terrains: TerrainShape[];
  grassPaint?: GrassPaint;
  grassLines?: GrassLines;
  spikeLines?: SpikeLines;
  decoPaint?: FoliagePaint;
  /** @deprecated first foliage paint (add | remove << 8 for both layers); read once into decoPaint */
  foliagePaint?: number[];
  objects: PlacedObject[];
  bodies: Body[];
  /** object id per system role */
  system: Partial<Record<SystemRole, string>>;
}

/** --- Baked export consumed by the Unity importer (LevelEditor/unity/LevelJsonImporter.cs) --- */

export interface BakedTile {
  x: number;
  y: number;
  /** palette index */
  p: number;
  f: TileFlags;
}

export interface BakedLayer extends Omit<TileLayer, 'tiles'> {
  cells: BakedTile[];
}

export interface BakedBody extends Omit<Body, 'layers' | 'terrains'> {
  layers: BakedLayer[];
}

export interface BakedLevel {
  format: 'platformer-level-baked';
  version: number;
  name: string;
  palette: PaletteEntry[];
  settings: LevelSettings;
  layers: BakedLayer[];
  objects: PlacedObject[];
  bodies: BakedBody[];
  system: Partial<Record<SystemRole, string>>;
}

export function emptyMovementVars(): MovementVars {
  return {
    speedOverride: 0, accOverride: 0, speedEnum: 0, accEnum: 0,
    returnSpeedOverride: 0, returnAccOverride: 0, returnSpeedEnum: 0, returnAccEnum: 0,
  };
}

export function flagsFromMatrix(e00: number, e01: number, e10: number, e11: number): TileFlags {
  // Find flags such that matrixFromFlags(flags) == given (rounded) matrix
  const m = [Math.round(e00), Math.round(e01), Math.round(e10), Math.round(e11)];
  for (let f = 0; f < 8; f++) {
    const r = matrixFromFlags(f);
    if (r[0] === m[0] && r[1] === m[1] && r[2] === m[2] && r[3] === m[3]) return f;
  }
  return 0;
}

/** Returns [e00, e01, e10, e11] of the Unity tile matrix (x' = e00 x + e01 y, y' = e10 x + e11 y). */
export function matrixFromFlags(f: TileFlags): [number, number, number, number] {
  let a = 1, b = 0, c = 0, d = 1;
  if (f & 4) { a = 0; b = 1; c = 1; d = 0; }
  if (f & 1) { a = -a; b = -b; }
  if (f & 2) { c = -c; d = -d; }
  return [a, b, c, d];
}
